import { useState, useEffect, useRef, useMemo } from "react";
import { useLeads } from "@/hooks/useLeads";
import { useAuth } from "@/contexts/AuthContext";
import { useTasks } from "@/hooks/useTasks";
import { LoanStatus, LOAN_STATUS_CONFIG, getLegacyCustomerName } from "@/types";
import { LeadCard } from "./LeadCard";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { searchItems, enrichWithResolvedFields } from "@/lib/searchUtils";
import { getUserName } from "@/data/mockData";

const pipelineStages: LoanStatus[] = [
  "new_lead",
  "in_progress",
  "ip_sanction",
  "logged_in",
  "query",
  "sanctioned",
  "verification_pending",
  "disbursal_pending",
  "partial_disbursed",
  "disbursed",
  "rejected",
  "otc",
  "payout_released",
  "closed",
  "removed",
  "followup_later",
];

interface LeadPipelineProps {
  initialFilter?: LoanStatus | "all";
  searchQuery?: string;
  ownershipFilter?: 'all' | 'created' | 'created_shared'; // Filter by creation/sharing
}

export function LeadPipeline({ initialFilter = "all", searchQuery = "", ownershipFilter = "all" }: LeadPipelineProps) {
  const { leads } = useLeads();
  const { user } = useAuth();
  const { tasks } = useTasks();
  const [activeFilter, setActiveFilter] = useState<LoanStatus | "all">(initialFilter);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Compute pending reminders per lead
  const pendingRemindersByLead = useMemo(() => {
    const map: Record<string, number> = {};
    tasks.forEach(task => {
      if (task.leadId && !task.completed) {
        map[task.leadId] = (map[task.leadId] || 0) + 1;
      }
    });
    return map;
  }, [tasks]);

  // Update activeFilter when initialFilter changes (e.g., from navigation)
  useEffect(() => {
    setActiveFilter(initialFilter);
  }, [initialFilter]);

  // Auto-scroll to selected stage
  useEffect(() => {
    if (activeFilter !== "all" && scrollContainerRef.current) {
      const selectedButton = scrollContainerRef.current.querySelector(
        `[data-status="${activeFilter}"]`
      );
      
      if (selectedButton) {
        selectedButton.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    }
  }, [activeFilter]);

  // Stages to exclude from "All" filter
  const excludedFromAllStages: LoanStatus[] = ["partial_disbursed", "disbursed", "otc", "payout_released", "closed", "removed", "followup_later"];

  // Filter by search query first
  const searchFilteredLeads = useMemo(() => {
    if (!searchQuery) return leads;
    
    // Enrich leads with resolved creator names for searchability
    const enrichedLeads = enrichWithResolvedFields(leads, {
      createdBy: (userId) => getUserName(userId),
    });
    
    return searchItems(
      searchQuery,
      enrichedLeads,
      [
        'firstName',
        'lastName',
        'mobile',
        'customerCity',
        'customerPincode',
        'lender',
        'email',
        'panCard',
        'loanType',
        'employmentType',
        'propertyCity',
        'propertyPincode',
        'propertyAddress',
        'companyName',
        'assignedBank',
        'existingLender',
        'createdBy_resolved', // Search by resolved creator name
      ]
    );
  }, [searchQuery, leads]);

  // Apply ownership filter (created by or shared with current user)
  const ownershipFilteredLeads = useMemo(() => {
    if (ownershipFilter === 'all' || !user) return searchFilteredLeads;
    
    const userId = user.id;
    
    if (ownershipFilter === 'created') {
      // Only leads created by current user
      return searchFilteredLeads.filter(lead => lead.createdBy === userId);
    }
    
    if (ownershipFilter === 'created_shared') {
      // Leads created by or shared with current user
      return searchFilteredLeads.filter(lead => 
        lead.createdBy === userId || lead.sharedWith?.includes(userId)
      );
    }
    
    return searchFilteredLeads;
  }, [searchFilteredLeads, ownershipFilter, user]);

  // Apply filter logic: exclude completed stages only from "All" view
  const filteredLeads = activeFilter === "all"
    ? ownershipFilteredLeads.filter(lead => !excludedFromAllStages.includes(lead.status))
    : ownershipFilteredLeads.filter(l => l.status === activeFilter);

  // Count leads by status (from all ownership filtered leads, not just active ones)
  const statusCounts = ownershipFilteredLeads.reduce((acc, lead) => {
    acc[lead.status] = (acc[lead.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);


  // Get background colors for tabs
  const getTabBgColor = (status: LoanStatus) => {
    const config = LOAN_STATUS_CONFIG[status];
    if (config.badgeVariant === 'success') return 'bg-success/10';
    if (config.badgeVariant === 'warning') return 'bg-warning/10';
    if (config.badgeVariant === 'destructive') return 'bg-destructive/10';
    if (config.badgeVariant === 'info') return 'bg-info/10';
    return 'bg-secondary';
  };

  return (
    <div className="space-y-4">
      {/* Filter pills - horizontal scroll on mobile */}
      <div ref={scrollContainerRef} className="overflow-x-auto -mx-4 px-4 pb-2 no-scrollbar">
        <div className="flex gap-2 min-w-max">
          <button
            onClick={() => setActiveFilter("all")}
            className={cn(
              "flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium transition-all",
              activeFilter === "all"
                ? "bg-primary text-primary-foreground shadow-md"
                : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
            )}
          >
            All
            <Badge variant="secondary" className="bg-background/20 text-current">
              {searchFilteredLeads.filter(lead => !excludedFromAllStages.includes(lead.status)).length}
            </Badge>
          </button>
          
          {pipelineStages.map((status) => {
            const config = LOAN_STATUS_CONFIG[status];
            const count = statusCounts[status] || 0;

            return (
              <button
                key={status}
                data-status={status}
                onClick={() => setActiveFilter(status)}
                className={cn(
                  "flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium transition-all whitespace-nowrap",
                  activeFilter === status
                    ? "bg-primary text-primary-foreground shadow-md"
                    : cn(getTabBgColor(status), "text-secondary-foreground hover:opacity-80")
                )}
              >
                {config.label}
                <Badge variant="secondary" className="bg-background/20 text-current text-xs">
                  {count}
                </Badge>
              </button>
            );
          })}
        </div>
      </div>

      {/* Lead list */}
      <motion.div
        key={activeFilter}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="space-y-3"
      >
        {filteredLeads.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground bg-secondary/30 rounded-xl">
            No leads in this stage
          </div>
        ) : (
          filteredLeads.map((lead) => (
            <LeadCard key={lead.id} lead={lead} pendingReminders={pendingRemindersByLead[lead.id] || 0} />
          ))
        )}
      </motion.div>
    </div>
  );
}
